import { useState, useEffect } from "react";
import { AlertMessagesService, Utils } from "../../../shared";
import { CampaignDetailService } from "../services/campaignDetails-service";
import { parseCampaignExcelFile, isValidExcelFile } from "../utils/excelParser";

/**
 * Custom hook to handle campaign file upload and parsing
 * @param {Function} onUploadSuccess - Callback to execute on successful upload
 * @returns {Object} - { parsedData, isProcessing, handleUpload }
 */
export const useCampaignFileUpload = (onUploadSuccess) => {
  const [parsedData, setParsedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handles file selection and parsing
   * @param {Event} event - The file-upload custom event
   */
  const handleFileChange = async (event) => {
    const fileWrapper = event.detail?.files?.[0];
    const file = fileWrapper?.file;
    
    if (!file) {
      console.error('No file found in event');
      return;
    }

    // Validate file type
    if (!isValidExcelFile(file)) {
      AlertMessagesService.showErrorToastMessage("Please upload a valid Excel or CSV file");
      return;
    }
    
    setIsProcessing(true);

    try {
      const clientId = Utils.getRouteParam("clientid");
      const parsed = await parseCampaignExcelFile(file, clientId);
      
      if (!parsed || parsed.length === 0) {
        AlertMessagesService.showErrorToastMessage("The file is empty or contains no valid data");
        setParsedData(null);
        return;
      }
      
      setParsedData(parsed);
      AlertMessagesService.showSuccessToastMessage(`Successfully parsed ${parsed.length} records`);
    } catch (error) {
      AlertMessagesService.showErrorToastMessage(error.message || "Failed to parse file");
      setParsedData(null);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Uploads the parsed data to the server
   */
  const handleUpload = async () => {
    if (!parsedData || parsedData.length === 0) {
      AlertMessagesService.showErrorToastMessage("No data to upload");
      return;
    }

    try {
      setIsProcessing(true);
      await CampaignDetailService.uploadCampaignTemplate(parsedData);
      AlertMessagesService.showSuccessToastMessage("Campaign data uploaded successfully");
      
      // Reset state
      setParsedData(null);
      
      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      AlertMessagesService.showErrorToastMessage(error.message || "Failed to upload campaign data");
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Resets the upload state
   */
  const resetUpload = () => {
    setParsedData(null);
    setIsProcessing(false);
  };

  // Set up event listener for file-upload custom event
  useEffect(() => {
    document.addEventListener('file-upload', handleFileChange);
    return () => {
      document.removeEventListener('file-upload', handleFileChange);
    };
  }, []);

  return {
    parsedData,
    isProcessing,
    handleUpload,
    resetUpload
  };
};

